import { Box, Fade } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  LearningPathCardDimensions,
  LearningPathCardSkeleton,
  LearningPathCardVariants,
  LearningPathVerticalCard,
} from '@/components';
import { EVENT_CONSTANTS } from '@/services';
import { LearningPathWithDuration, Referrer } from '@/types';

import {
  GRID_CHUNK_SIZE,
  SCROLL_THRESHOLD,
  SKELETON_CARD_COUNT,
} from './constants';

interface CardGridProps {
  lps: LearningPathWithDuration[];
  isLoading?: boolean;
  referrer?: Referrer;
}

export const LearningPathCardGrid = ({
  lps = [],
  isLoading = false,
  referrer = {},
}: CardGridProps) => {
  const [visibleCardCount, setVisibleCardCount] = useState(GRID_CHUNK_SIZE);

  useEffect(() => {
    const loadMore = () => {
      if (visibleCardCount >= lps.length) return;

      const currentScrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const scrollThreshold =
        (document.scrollingElement?.scrollHeight || 0) - SCROLL_THRESHOLD;

      if (currentScrollPosition >= scrollThreshold) {
        setVisibleCardCount(
          Math.min(visibleCardCount + GRID_CHUNK_SIZE, lps.length),
        );
      }
    };

    window.addEventListener('scroll', loadMore);
    return () => window.removeEventListener('scroll', loadMore);
  }, [visibleCardCount, lps.length]);

  const visibleLps = lps.slice(0, visibleCardCount);

  return (
    <Box
      sx={[
        {
          display: 'grid',
          mt: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: `repeat(auto-fill, minmax(${Math.round(LearningPathCardDimensions[LearningPathCardVariants.VERTICAL].width * 0.9)}px, 1fr))`,
          },
          gap: 1,
        },
      ]}>
      {!isLoading
        ? visibleLps?.map((lp, index) => (
            <Fade
              key={index}
              in
              timeout={Math.log10((index % GRID_CHUNK_SIZE) + 2) * 1000}>
              <Box>
                <LearningPathVerticalCard
                  stretch
                  learningPath={lp}
                  referrer={{
                    ...referrer,
                    component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_CARD,
                    position: index + 1,
                  }}
                />
              </Box>
            </Fade>
          ))
        : Array.from(Array(SKELETON_CARD_COUNT)).map((_, index) => (
            <Fade
              key={index}
              in
              timeout={Math.log10((index % GRID_CHUNK_SIZE) + 2) * 1000}>
              <Box sx={{ minWidth: 0 }}>
                <LearningPathCardSkeleton
                  stretch
                  variant={LearningPathCardVariants.VERTICAL}
                />
              </Box>
            </Fade>
          ))}
    </Box>
  );
};
